import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { catchError, mergeMap, map, of, switchMap, take } from 'rxjs';
import * as WagerActions from './wager.actions';
import { fetch } from '@nx/angular';
import * as _ from 'lodash';

// Local library
import {
  TotePoolInfoAPIService,
  ToteProgramAPIService,
  ToteResultAPIService,
  ToteWageringAPIService,
  VideoStreamingAPIService
} from '@amwager/data-access';
import {
  IGetEventDetailsCallResponse,
  IGetEventsDetailsResponse,
  IGetPoolMatrixResponse,
  IGetPoolsTotalsResponse,
  IGetWillPaysResponse,
  IRCNGetLinkResponse,
  IResponseModel,
  IToteOddsMatrixCallResponse,
  IToteWageringGetCountersResponse
} from '@amwager/util-ts';
import { AccountFacade } from '@amwager/account/data-access';
import { WagerFacade } from './wager.facade';
import { IGetBets } from './wager.models';


@Injectable()
export class WagerEffects {

  constructor(
    private readonly actions$: Actions,
    private readonly toteProgramAPIService: ToteProgramAPIService,
    private readonly toteResultAPIService: ToteResultAPIService,
    private readonly toteWageringAPIService: ToteWageringAPIService,
    private readonly totePoolInfoAPIService: TotePoolInfoAPIService,
    private readonly videoStreamingAPIService: VideoStreamingAPIService,
    private readonly accountFacade: AccountFacade,
    private readonly wagerFacade: WagerFacade,
  ) {}

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.initWagerState),
      switchMap(() => of(WagerActions.loadWagerStateSuccess())),
      catchError((error) => {
        console.error('Error', error);
        return of(WagerActions.loadWagerStateFailure({ error }));
      })
    )
  );

  getRunners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getRunnersAPI),
      fetch({
        id: () => 'getRunners',
        run: ({ runnerPrefix }) => this.toteProgramAPIService.geRunners$(runnerPrefix)
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return WagerActions.getRunnersAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getRunnersAPIFailed({ errorResponse }),
      })
    )
  );

  getEquibaseChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getEquibaseChangesAPI),
      fetch({
        id: () => 'getEquibaseChanges',
        run: ({ equibaseEventId, raceDate }) => this.toteProgramAPIService.getEquibaseChanges$(
          equibaseEventId,
          raceDate
        )
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return WagerActions.getEquibaseChangesAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getEquibaseChangesAPIFailed({ errorResponse }),
      })
    )
  );

  getAllEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getAllEventsAPI),
      fetch({
        id: () => 'getAllEvents',
        run: () => this.toteProgramAPIService.getAllEvents$()
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              if (!response) return WagerActions.getAllEventsAPIFailed({ errorResponse: "No Data" });

              const { RaceMorningMatrix, ToteResponse } = response;
              return WagerActions.getAllEventsAPISuccess({
                raceMorningMatrix: RaceMorningMatrix,
                toteResponse: ToteResponse
              });
            })
          )
        ,
        onError: ({}, errorResponse) => {

          WagerActions.getAllEventsAPIFailed({ errorResponse })
        },
      })
    )
  );

  getAllEventsByDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getAllEventsByDateAPI),
      fetch({
        id: () => 'getAllEventsByDate',
        run: ({ requestedDateStr }) => this.toteProgramAPIService.getAllEventsByDate$(
          requestedDateStr
        )
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }


              return WagerActions.getAllEventsByDateAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getAllEventsByDateAPIFailed({ errorResponse }),
      })
    )
  );

  getAllTracksForGeo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getAllTracksForGeoAPI),
      fetch({
        id: () => 'getAllTracksForGeo',
        run: () => this.toteProgramAPIService.getAllTracksForGeo$()
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return WagerActions.getAllTracksForGeoAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getAllTracksForGeoAPIFailed({ errorResponse }),
      })
    )
  );

  getEventDetailLite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getEventDetailLiteAPI),
      fetch({
        id: () => 'getEventDetailLite',
        run: ({ eventId }) => this.toteProgramAPIService.getEventDetailLite$(eventId)
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return WagerActions.getEventDetailLiteAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getEventDetailLiteAPIFailed({ errorResponse }),
      })
    )
  );

  getAllEventsNameAndId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getAllEventsNameAndIdAPI),
      fetch({
        id: () => 'getAllEventsNameAndId',
        run: () => this.toteProgramAPIService.getAllEventsNameAndId$()
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return WagerActions.getAllEventsNameAndIdAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getAllEventsNameAndIdAPIFailed({ errorResponse }),
      })
    )
  );

  getEventsDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getEventsDetailsAPI),
      fetch({
        id: () => 'getEventsDetails',
        run: ({ runId }) => this.toteProgramAPIService.getEventsDetails$(runId)
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

            // map(( response ) => {

              return WagerActions.getEventsDetailsAPISuccess({
                successResponse: response as IGetEventsDetailsResponse[]
              });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getEventsDetailsAPIFailed({ errorResponse }),
      })
    )
  );

  getCompressedEventsDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getCompressedEventsDetailsAPI),
      fetch({
        id: () => 'getCompressedEventsDetails',
        run: ({ callOrigin, runId }) => this.toteProgramAPIService.getCompressedEventsDetails$(
          callOrigin,
          runId
        )
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return WagerActions.getCompressedEventsDetailsAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getCompressedEventsDetailsAPIFailed({ errorResponse }),
      })
    )
  );

  getEventDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getEventDetailAPI),
      fetch({
        id: () => 'getEventDetail',
        run: ({ eventId, runId, callOrigin }) => this.toteProgramAPIService.getEventDetail$(
          eventId,
          runId,
          callOrigin
        )
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return WagerActions.getEventDetailAPISuccess({
                eventId,
                successResponse: response
             });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getEventDetailAPIFailed({ errorResponse }),
      })
    )
  );

  getRace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getRaceAPI),
      fetch({
        id: () => 'getRace',
        run: ({ raceId, runId, eventId }) => this.toteProgramAPIService.getRace$(
          raceId,
          runId,
          eventId
        )
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return WagerActions.updateRace({
                sourceInitiator: 'WagerEffects:getRace',
                data: response?.ToteResponse
              });
              // return WagerActions.getRaceAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getRaceAPIFailed({ errorResponse }),
      })
    )
  );


  getResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getRaceResultAPI),
      fetch({
        id: () => 'getResult',
        run: ({
          raceId, runId, eventId, getPoolTotals
        }) => this.toteResultAPIService.getResult$({
          raceId, runId, eventId, getPoolTotals
        })
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return WagerActions.getRaceResultAPISuccess({ successResponse: response, eventId });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getRaceResultAPIFailed({ errorResponse }),
      })
    )
  );


  // shared common state
//   updateRace$ = createEffect(() =>
//   this.actions$.pipe(
//     ofType(CommonActions.pusherCommand),
//     fetch({
//       id: () => 'updateRace',
//       run: ({ raceId, runId, eventId }) => this.toteProgramAPIService.getRace$(
//         raceId,
//         runId,
//         eventId
//       )
//         .pipe(
//           // map(({ Model: response }) => {
//           // eslint-disable-next-line @typescript-eslint/no-explicit-any
//           map((response: any) => {
//             // map(({ Model: response }) => {

//             try {
//               // eslint-disable-next-line no-unsafe-optional-chaining
//               if ("Model" in response) {
//                 response = response?.Model;
//               }
//             } catch (error) {
//               console.error("Model:API");
//             }

//             return WagerActions.getRaceAPISuccess({ successResponse: response });
//           })
//         )
//       ,∏
//       onError: ({}, errorResponse) => WagerActions.getRaceAPIFailed({ errorResponse }),
//     })
//   )
// );

  getCounter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getCountersAPI),
      fetch({
        id: () => 'get-counter',
        run: ({
          runId,
          sourceInitiator
        }) => this.toteWageringAPIService.getCounters$(runId)
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
            // map(({ Model: response }) => {
              // console.log(response);

              // try {
              //   // eslint-disable-next-line no-unsafe-optional-chaining
              //   if ("Model" in response) {
              //     response = response?.Model;
              //   }
              // } catch (error) {
              //   console.error("Model:API");
              // }

              if (response === undefined)
                response = {};
              else
                response = response.Counter;

              return WagerActions.getCountersAPISuccess({
                successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return WagerActions.getCountersAPIFailed({ errorResponse });
        }
      })
    )
  );

  // bet section
  getBets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getBetsAPI),
      fetch({
        // id: () => 'get-bets',
        run: ({
          bet,
          status
        }) => this.toteWageringAPIService.getTicketsSlim$(bet)
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
            // map(({ Model: response }) => {

              // try {
              //   // eslint-disable-next-line no-unsafe-optional-chaining
              //   if ("Model" in response) {
              //     response = response?.Model;
              //   }
              // } catch (error) {
              //   console.error("Model:API");
              // }

              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              let tickets: any[] = [];
              // let tickets = {};
              if (response.ParentTickets !== undefined) {
                // solution 1
                // tickets = response.ParentTickets;
                // solution 2
                // tickets = Object.keys(response.ParentTickets).map(key => ({ key, values: response[key] }));
                // solution 3
                // Object.keys(response.ParentTickets).forEach(key => {
                //   console.log(response.ParentTickets[key]);
                //   tickets.push(response.ParentTickets[key]);
                // });
                // solution 4
                // Object.keys(response.ParentTickets).forEach(key => {
                //   tickets = tickets.concat(response.ParentTickets[key])
                // });

                // tickets = _.map(response.ParentTickets, (data, key) => ({
                //   ParentId: key,
                //   TicketId: data[0].TicketId,
                //   CreatedAt: data[0].CreatedAt,
                //   TicketStatus: data[0].TicketStatus,
                //   RunId: data[0].RunId,
                //   EventId: data[0].EventId,
                //   RaceId: data[0].RaceId,
                //   BaseAmount: data[0].BaseAmount,
                //   Modifier: data[0].Modifier,
                //   DutchType: data[0].DutchType,
                //   PoolId: data[0].PoolId,
                //   Runners: data[0].Runners,
                //   TicketAmount: data[0].TicketAmount,
                //   Condition: data[0].Condition,
                //   NetPayoff: data[0].NetPayoff,
                //   IsCancelled: data[0].IsCancelled,
                //   IsError: data[0].IsError
                // }));

                tickets = _.flatMap(response.ParentTickets, (items: any[], ParentId: any) =>
                  items.map((item: any) => ({
                      ParentId,
                      ...item
                  }))
                );

                // console.log('tickets ', tickets);

              }
              return WagerActions.getBetsAPISuccess({
                successResponse: tickets,
                status
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return WagerActions.getBetsAPIFailed({ errorResponse });
        }
      })
    )
  );

  submitSavedBet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.submitSavedBetAPI),
      fetch({
        id: () => 'submit-saved-bet',
        run: ({ ticket }) => this.toteWageringAPIService.submitStackTickets$({
          EventId: ticket.EventId,
          ParentStackTicketsIds: [ticket.ParentId],
          RaceId: ticket.RaceId,
          RunId: ticket.RunId,
          StackTicketsIds: [ticket.TicketId],
          Location: ticket.Location
        })
          .pipe(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
            map(( response: any ) => {
              // console.log(payload);
              // const ticket = payload;
              this.accountFacade.getBalance("WagerEffects:submitSavedBet");


              const statuses = {
                1: 'saved',
                3: 'active',
              };

              _.map([1, 3], (status) => {
                this.wagerFacade.getBetsAPI(
                  {
                    runId: ticket.RunId,
                    ticketStatus: status,
                    offset: 0,
                    next: 500
                  },
                  (statuses as any)[status],
                  'WagerEffects:submitSavedBet$'
                );
              });

              // temp
              return WagerActions.submitSavedBetAPISuccess({
                ticket, successResponse: response
              });

              // return WagerActions.submitSavedBetAPISuccess({
              //   payload, successResponse: response
              // });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return WagerActions.submitSavedBetAPIFailed({ errorResponse });
        }
      })
    )
  );

  removeSavedBet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.removeSavedBetAPI),
      fetch({
        id: () => 'remove-saved-bet',
        run: ({ ticket }) => this.toteWageringAPIService.removeStackedTicket$({
          StackIds: [ticket.TicketId],
          EventId: ticket.EventId,
          RaceId: ticket.RaceId,
          RunId: ticket.RunId
        })
          .pipe(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
            map(( response: any ) => {
              // const ticket = payload;
              this.accountFacade.getBalance("WagerEffects:removeSavedBet");


              const statuses = {
                1: 'saved',
              };

              _.map([1], (status) => {
                this.wagerFacade.getBetsAPI(
                  {
                    runId: ticket.RunId,
                    ticketStatus: status,
                    offset: 0,
                    next: 500
                  },
                  (statuses as any)[status],
                  'WagerEffects:submitSavedBet$'
                );
              });

              return WagerActions.removeSavedBetAPISuccess({
                ticket, successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return WagerActions.removeSavedBetAPIFailed({ errorResponse });
        }
      })
    )
  );

  cancelBet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.cancelBetAPI),
      fetch({
        id: () => 'cancel-bet',
        run: ({ ticket }) => this.toteWageringAPIService.cancelWager$(
          ticket.TicketId
        )
          .pipe(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
            map(( response: any ) => {
              // console.log('WagerEffects:cancelBet', {ticket, response});

              this.accountFacade.getBalance("WagerEffects:cancelBet");

              const statuses = {
                3: 'active',
              };

              _.map([3], (status) => {
                this.wagerFacade.getBetsAPI(
                  {
                    runId: ticket.RunId,
                    ticketStatus: status,
                    offset: 0,
                    next: 500
                  },
                  (statuses as any)[status],
                  'WagerEffects:cancelBet$'
                );
              });

              return WagerActions.cancelBetAPISuccess({
                ticket, successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return WagerActions.cancelBetAPIFailed({ errorResponse });
        }
      })
    )
  );

  saveBet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.saveBetAPI),
      fetch({
        // id: () => 'save-bet',
        run: ({
          betStackTicket
        }) => this.toteWageringAPIService.stackSingleTicket$(betStackTicket)
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
            // map(({ Model: response }) => {

              // try {
              //   // eslint-disable-next-line no-unsafe-optional-chaining
              //   if ("Model" in response) {
              //     response = response?.Model;
              //   }
              // } catch (error) {
              //   console.error("Model:API");
              // }

              this.accountFacade.getBalance("WagerEffects:saveBet");

              const statuses = {
                1: 'saved'
              };

              _.map([1], (status) => {
                this.wagerFacade.getBetsAPI(
                  {
                    runId: betStackTicket.RunId,
                    ticketStatus: status,
                    offset: 0,
                    next: 500
                  },
                  (statuses as any)[status],
                  'WagerEffects:saveBet$'
                );
              });

              return WagerActions.saveBetAPISuccess({
                successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return WagerActions.saveBetAPIFailed({ errorResponse });
        }
      })
    )
  );

  betNow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.betNowAPI),
      fetch({
        id: () => 'bet-now',
        run: ({
          betTicket
        }) => this.toteWageringAPIService.issueSingleTicket$(betTicket)
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
            // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              this.accountFacade.getBalance("WagerEffects:betNow");

              const statuses = {
                3: 'active'
              };

              _.map([3], (status) => {
                this.wagerFacade.getBetsAPI(
                  {
                    runId: betTicket.RunId,
                    ticketStatus: status,
                    offset: 0,
                    next: 500
                  },
                  (statuses as any)[status],
                  'WagerEffects:betNow$'
                );
              });

              return WagerActions.betNowAPISuccess({
                successResponse: response
              });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return WagerActions.betNowAPIFailed({ errorResponse });
        }
      })
    )
  );

  getPoolMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getPoolMatrixAPI),
      // normal fetch with unorder
      mergeMap(({ requests }) => this.totePoolInfoAPIService.getPoolMatrix$(requests)
        .pipe(
          map((response: IGetPoolMatrixResponse) => {
            try {
              // eslint-disable-next-line no-unsafe-optional-chaining
              if ("Model" in response) {
                response = response?.Model as IGetPoolMatrixResponse;
              }
            } catch (error) {
              console.error("Model:API:getPoolMatrixAPI");
            }

            return WagerActions.getPoolMatrixAPISuccess({
              eventId: requests?.eventId,
              raceId: requests?.raceId,
              poolId: requests?.poolId,
              successResponse: response
            });
          }),
          catchError((data, errorResponse) => of(
            WagerActions.getPoolMatrixAPIFailed({
              poolId: data?.requests?.poolId,
              errorResponse
            })
          ))
        )
      )
    )
  );

  getPoolsTotals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getPoolsTotalsAPI),
      // normal fetch with unorder
      mergeMap(({ requests }) => this.totePoolInfoAPIService.getPoolsTotals$(requests)
        .pipe(
          map((response: IGetPoolsTotalsResponse) => {
            try {
              // eslint-disable-next-line no-unsafe-optional-chaining
              if ("Model" in response) {
                response = response?.Model as IGetPoolsTotalsResponse;
              }
            } catch (error) {
              console.error("Model:API:getPoolsTotalsAPI");
            }

            return WagerActions.getPoolsTotalsAPISuccess({
              eventId: requests?.eventId,
              raceId: requests?.raceId,
              successResponse: response
            });
          }),
          catchError(({}, errorResponse) => of(
            WagerActions.getPoolsTotalsAPIFailed({ errorResponse })
          ))
        )
      )
    )
  );

  getWillPays$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getWillPaysAPI),
      fetch({
        id: () => 'getWillPays',
        run: ({ requests }) => this.totePoolInfoAPIService.getWillPays$(
          requests?.raceId, requests?.eventId, requests?.runId
        )
          .pipe(
            map((response: IGetWillPaysResponse) => {
              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model as IGetWillPaysResponse;
                }
              } catch (error) {
                console.error("Model:API:getWillPaysAPI");
              }

              return WagerActions.getWillPaysAPISuccess({
                eventId: requests?.eventId,
                raceId: requests?.raceId,
                successResponse: response
              });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getWillPaysAPIFailed({ errorResponse }),
      })
    )
  );

  getRaceAllOddsMatrixes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getRaceAllOddsMatrixesAPI),
      // normal fetch with unorder
      mergeMap(({ requests, pool }) => this.totePoolInfoAPIService.getRaceAllOddsMatrixes$(requests)
        .pipe(
          map((response: IToteOddsMatrixCallResponse)  => {
            try {
              // eslint-disable-next-line no-unsafe-optional-chaining
              if ("Model" in response) {
                response = response?.Model as IToteOddsMatrixCallResponse;
              }
            } catch (error) {
              console.error("Model:API:getRaceAllOddsMatrixes");
            }

            return WagerActions.getRaceAllOddsMatrixesAPISuccess({
              eventId: requests?.eventId,
              raceId: requests?.raceId,
              pool,
              successResponse: response
            });
          }),
          catchError(({pool}, errorResponse) => of(
            WagerActions.getRaceAllOddsMatrixesAPIFailed({
              pool,
              errorResponse
            })
          ))
        )
      )
    )
  );

  updateRaceFinal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.updateRaceFinal),
      // normal fetch with unorder
      mergeMap(({ data }) => this.toteProgramAPIService.getEventDetail$(data?.EventId, data?.RunId)
        .pipe(
          map((response: IResponseModel<IGetEventDetailsCallResponse> | IGetEventDetailsCallResponse) => {
            try {
              // eslint-disable-next-line no-unsafe-optional-chaining
              if ("Model" in response) {
                response = response?.Model as IGetEventDetailsCallResponse;
              }
            } catch (error) {
              console.error("Model:API:IGetEventDetailsCallResponse");
            }

            return WagerActions.getEventDetailAPISuccess({
              eventId: data?.EventId,
              successResponse: response,
              raceId: data?.RaceId
            });
          }),
          catchError(({}, errorResponse) => of(
            WagerActions.getEventDetailAPIFailed({
              errorResponse
            })
          ))
        )
      )
    )
  );

  updateRacePost$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.updateRacePost),
      switchMap((pusherData) => {

        // console.log('pusher:effects:updateRacePost:', pusherData)

        const data = pusherData?.data;

        // submit saved bet after event race is posted
        // TODO: check if posted or 0 MTP, preference condition might be from Pusher command STC
        this.wagerFacade.getBets$()
          .pipe(take(1))
          .subscribe((myBets: IGetBets) => {
            if (_.isEmpty(myBets)) return;

            _.map(myBets?.saved, (ticket) => {
              if (ticket?.EventId === data?.EventId && ticket?.RaceId === data?.RaceId) {
                this.wagerFacade.submitSavedBetAPI(ticket);
              }
            });
          });

        return of()
      }),
      catchError((error) => of(WagerActions.setError({ error })))
    )
  );

  getReplays$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getReplaysAPI),
      fetch({
        // id: () => 'get-replays',
        run: ({ requests }) => this.videoStreamingAPIService.getRCNReplayVideosByFilters$(requests)
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map(( response: any ) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              return WagerActions.getReplaysAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return WagerActions.getReplaysAPIFailed({ errorResponse });
        }
      })
    )
  );

  getRaceVideoLive$ = createEffect(() =>
   {
    const mapCB = (toteEventId: string) => map((response: IRCNGetLinkResponse) => {
      try {
        // eslint-disable-next-line no-unsafe-optional-chaining
        if ("Model" in response) {
          response = response?.Model as IRCNGetLinkResponse;
        }
      } catch (error) {
        console.error("Model:API:getRaceVideoLive");
      }

      // test separate live video
      this.wagerFacade.setEventLiveVideo(response);

      return WagerActions.getEventRaceVideoLiveAPISuccess({
        successResponse: response,
        eventId: toteEventId
      });
    });

    return this.actions$.pipe(
      ofType(WagerActions.getEventRaceVideoLiveAPI),
      fetch({
        id: () => 'getRaceVideoLive',
        run: ({ request, typeVideo }) => {
          if (typeVideo === 'IGB') {
            return this.videoStreamingAPIService.getIGBLiveVideoLink$(
              request?.toteEventId
            )
              .pipe(
                mapCB(request?.toteEventId)
              )
          }
          else {
            return this.videoStreamingAPIService.getRCNLiveVideoLinkH2L$(
              request?.toteEventId, request?.width, request?.height
            )
              .pipe(
                mapCB(request?.toteEventId)
              )
          }
        },
        onError: ({}, errorResponse) => WagerActions.getEventRaceVideoLiveAPIFailed({
          errorResponse
        }),
      })
    )
   }
  );

  getRaceVideoReplay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WagerActions.getEventRaceVideoReplayAPI),
      fetch({
        id: () => 'getRaceVideoReplay',
        run: ({ request }) => this.videoStreamingAPIService.getRCNReplayVideoLink$(request)
          .pipe(
            map((response: IRCNGetLinkResponse) => {
              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model as IRCNGetLinkResponse;
                }
              } catch (error) {
                console.error("Model:API:getRaceVideoReplay");
              }

              return WagerActions.getEventRaceVideoReplayAPISuccess({
                successResponse: response,
                eventId: request?.toteEventId || ''
              });
            })
          )
        ,
        onError: ({}, errorResponse) => WagerActions.getEventRaceVideoReplayAPIFailed({
          errorResponse
        }),
      })
    )
  );

}
